'use client';

import { useEffect, useState, useCallback } from 'react';
import { useAuth } from '@/platform/auth/auth-context';
import { fetchWithAuth } from '@/lib/auth/fetch-with-auth';
import { Package, Briefcase } from 'lucide-react';
import React from 'react';

export interface ProductBrandingInfo {
  name: string;
  tagline: string;
  iconUrl: string | null;
  defaultIcon: React.ReactNode;
}

export interface ProductBranding {
  containerName: string;
  containerIconUrl: string | null;
  products: {
    productA: ProductBrandingInfo;
    productB: ProductBrandingInfo;
  };
}

export const DEFAULT_BRANDING: ProductBranding = {
  containerName: 'Conscious Brand Index',
  containerIconUrl: null,
  products: {
    productA: {
      name: 'Conscious Brand Assessment',
      tagline: 'Evaluate your brand across six key indicators',
      iconUrl: null,
      defaultIcon: React.createElement(Package, { className: 'h-12 w-12 text-primary' }),
    },
    productB: {
      name: 'Brand Archetype Assessment',
      tagline: "Discover your brand's core identity",
      iconUrl: null,
      defaultIcon: React.createElement(Briefcase, { className: 'h-12 w-12 text-secondary' }),
    },
  },
};

export const SIDEBAR_DEFAULT_ICONS: Record<string, React.ReactNode> = {
  productA: React.createElement(Package, { className: 'h-5 w-5' }),
  productB: React.createElement(Briefcase, { className: 'h-5 w-5' }),
};

let cachedBranding: ProductBranding | null = null;
let cacheTimestamp = 0;
const CACHE_TTL = 60_000;

export function useProductBranding() {
  const { session } = useAuth();
  const [branding, setBranding] = useState<ProductBranding>(cachedBranding ?? DEFAULT_BRANDING);
  const [loading, setLoading] = useState(!cachedBranding);

  useEffect(() => {
    if (!session?.access_token) return;
    if (cachedBranding && Date.now() - cacheTimestamp < CACHE_TTL) {
      setBranding(cachedBranding);
      setLoading(false);
      return;
    }

    let cancelled = false;
    (async () => {
      try {
        const res = await fetchWithAuth('/api/admin/product-branding', session);
        if (!res.ok) return;
        const data = await res.json();
        if (cancelled) return;
        const settings = data?.settings ?? {};
        const resolved: ProductBranding = {
          containerName: settings.containerName || DEFAULT_BRANDING.containerName,
          containerIconUrl: settings.containerIconUrl ?? null,
          products: {
            productA: {
              name: settings.products?.productA?.name || DEFAULT_BRANDING.products.productA.name,
              tagline: settings.products?.productA?.tagline || DEFAULT_BRANDING.products.productA.tagline,
              iconUrl: settings.products?.productA?.iconUrl ?? null,
              defaultIcon: DEFAULT_BRANDING.products.productA.defaultIcon,
            },
            productB: {
              name: settings.products?.productB?.name || DEFAULT_BRANDING.products.productB.name,
              tagline: settings.products?.productB?.tagline || DEFAULT_BRANDING.products.productB.tagline,
              iconUrl: settings.products?.productB?.iconUrl ?? null,
              defaultIcon: DEFAULT_BRANDING.products.productB.defaultIcon,
            },
          },
        };
        cachedBranding = resolved;
        cacheTimestamp = Date.now();
        setBranding(resolved);
      } catch {
        // Use defaults on error
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => { cancelled = true; };
  }, [session?.access_token]);

  const refresh = useCallback(async () => {
    if (!session?.access_token) return;
    try {
      const res = await fetchWithAuth('/api/admin/product-branding', session);
      if (!res.ok) return;
      const data = await res.json();
      const settings = data?.settings ?? {};
      const resolved: ProductBranding = {
        containerName: settings.containerName || DEFAULT_BRANDING.containerName,
        containerIconUrl: settings.containerIconUrl ?? null,
        products: {
          productA: {
            name: settings.products?.productA?.name || DEFAULT_BRANDING.products.productA.name,
            tagline: settings.products?.productA?.tagline || DEFAULT_BRANDING.products.productA.tagline,
            iconUrl: settings.products?.productA?.iconUrl ?? null,
            defaultIcon: DEFAULT_BRANDING.products.productA.defaultIcon,
          },
          productB: {
            name: settings.products?.productB?.name || DEFAULT_BRANDING.products.productB.name,
            tagline: settings.products?.productB?.tagline || DEFAULT_BRANDING.products.productB.tagline,
            iconUrl: settings.products?.productB?.iconUrl ?? null,
            defaultIcon: DEFAULT_BRANDING.products.productB.defaultIcon,
          },
        },
      };
      cachedBranding = resolved;
      cacheTimestamp = Date.now();
      setBranding(resolved);
    } catch {
      // Keep current state
    }
  }, [session?.access_token]);

  return { branding, loading, refresh };
}

export function getProductIcon(productKey: 'productA' | 'productB', branding: ProductBranding, sizeClass: string): React.ReactNode {
  const info = branding.products[productKey];
  if (info.iconUrl) {
    return React.createElement('img', { src: info.iconUrl, alt: info.name, className: sizeClass });
  }
  const DefaultIcon = productKey === 'productA' ? Package : Briefcase;
  return React.createElement(DefaultIcon, { className: sizeClass });
}
